<template>
  <GenericSideMenu
    position="right"
    :title="showHeadline"
    :self-contained="true"
    @close="$emit('close')"
  >
    <div
      v-if="!globalContent.getHideSampleCartText && showContent !== ''"
      class="quickBuyMultipleContent type-sm text-text-subtle mb-16"
      v-html="showContent"
    />
    <div v-if="products.length">
      <QuickBuyItem
        v-for="(product) in products"
        :key="product.partNo"
        :product="product"
        :stock-label="getStockLabel(product.mainVariant)"
        :category-code="categoryCode"
        :class=" {
          'first:border-t-0': globalContent.getHideSampleCartText || showContent === ''
        }"
        @close="$emit('close')"
      />
    </div>

    <div v-if="extraText" class="flex p-16 gap-8 bg-bg-attention type-headline-sm mt-24 items-start">
      <img src="/icons/info.svg" class="w-20 relative top-1">
      <div>{{ extraText }}</div>
    </div>

    <!-- Sample size guide link (only for Samples category) -->
    <div
      v-if="categoryCode === 'Samples' && hasSizeGuideLink && sizeGuideUrl"
      class="mt-24 pt-16 border-t border-light"
    >
      <button
        class="flex items-center gap-8 type-sm text-dark hover:text-darkest transition-colors"
        @click="showSizeGuide = true"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M3 8.5 8.5 3 21 15.5 15.5 21 3 8.5Z" />
          <path d="M7 8.5 8.5 10M9.5 6 11.5 8M12 3.5 14 5.5M6 11.5 8 13.5" />
        </svg>
        <span class="underline hover:no-underline">
          {{ $t('mypage.sampleInventory.infoBanner.guideLink') }}
        </span>
      </button>
    </div>

    <SampleInventorySizeGuideModal
      v-if="showSizeGuide && sizeGuideUrl"
      :image-url="sizeGuideUrl"
      @close="showSizeGuide = false"
    />
  </GenericSideMenu>
</template>

<script setup lang="ts">
import GenericSideMenu from '~/components/menu/GenericSideMenu.vue';
import useApiFetch from '~/composables/useApiFetch';
import { CategoryDetails } from '#constants/types/norce';
import useAlgoliaFetch from '~/composables/useAlgoliaFetch';
import Product, { ProductModel } from '#models/product';
import QuickBuyItem from '~/components/product/QuickBuyItem.vue';
import SampleInventorySizeGuideModal from '~/components/account/sample-inventory/SampleInventorySizeGuideModal.vue';
import useStockStatus from '~/composables/useStockStatus';
import { useGlobalContentStore } from '~/store/globalContent';
const { apiGet } = useApiFetch();
const { $t } = useNuxtApp();

const { getExactVariantByPartNo, lastError,
  lastErrorCode } = useAlgoliaFetch();
const { getStockLabel } = useStockStatus();
const globalContent = useGlobalContentStore();

interface Props {
  partNos?: string[]; // loads from algolia, either partNos or productsIn must be set
  productsIn?: ProductModel[]; // If product data is already loaded
  headline?: string;
  content?: string;
  categoryCode?: string | null; // loads info about a category from a norce, replaces headline and content
}

const props = withDefaults(defineProps<Props>(), {
  partNos: () => [] as string[],
  productsIn: () => [] as ProductModel[],
  headline: '',
  content: '',
  categoryCode: null,
});
defineEmits<{
  (e: 'close'): void;
}>();

const showHeadline = ref(props.headline);
const showContent = ref(props.content);
const extraText = $t(`multibuy.${props.categoryCode}.needMore`) !== `multibuy.${props.categoryCode}.needMore`
  ? $t(`multibuy.${props.categoryCode}.needMore`) : null;

const products = ref<ProductModel[]>(props.productsIn?.length ? props.productsIn : []);

// Size guide for samples
const showSizeGuide = ref(false);
const guideLinkKey = 'mypage.sampleInventory.infoBanner.guideLink';
const guideLinkUrlKey = 'mypage.sampleInventory.infoBanner.guideLinkUrl';

const hasSizeGuideLink = computed(() => {
  const translated = $t(guideLinkKey);
  return translated && translated !== guideLinkKey;
});

const sizeGuideUrl = computed(() => {
  const url = $t(guideLinkUrlKey);
  return url && url !== guideLinkUrlKey ? url : null;
});

onMounted(()=> {
  if (props.categoryCode) {
    setTimeout(()=> {
      loadCategoryInfo();
    }, 100);
  }
  if (props.partNos.length) {
    setTimeout(()=> {
      loadProducts();
    }, 100);
  }
});

const loadCategoryInfo = async() => {
  console.log('loadCategoryInfo')

  const res = await apiGet<CategoryDetails[]>(
    `/applications/categories?code=${props.categoryCode}`
  );
  if (res) {
    const thisRes = res.find(f => f.code === props.categoryCode);
    if (thisRes) {
      const h1Content = thisRes.description.match(/<h1>(.*?)<\/h1>/)?.[1];
      showHeadline.value = h1Content && h1Content !== '' ? h1Content : thisRes.name;
      showContent.value = thisRes.description.replace(/<h1>.*?<\/h1>/, '');
    } else {
      console.log('Data loaded but no match in  loadCategoryInfo')
    }
  } else {
    console.log('Nothing loaded in loadCategoryInfo')

  }
  if (showHeadline.value === '' && props.categoryCode) {
    showHeadline.value = props.categoryCode
  }

};

const loadProducts = async() => {
  const res = await getExactVariantByPartNo(props.partNos);

  if (res) {
    products.value = res.hits.map(p => Product.create([p]));
  } else {
    console.log(lastError.value);
    console.log(lastErrorCode.value);
  }
};

</script>
